import type { MRT_ColumnDef } from "mantine-react-table-open";
import {
  MantineReactTable,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo } from "react";
import { getMinimalMRTOptions } from "../../../../mantine/getMinimalMRTOptions";
import type { VersorgerstatusPeriod } from "../../../../utils/backend-types";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { getSupplierStatusLabel } from "./utils/getSupplierStatusLabel";

interface ChangeableSupplierStateHistoryProps {
  versorgerstatusPeriods: Array<VersorgerstatusPeriod>;
}

type SupplierStateHistoryRow = {
  firstDate: string | null;
  lastDate: string | null;
  statusLabel: string;
};

function ChangeableSupplierStateHistory({
  versorgerstatusPeriods
}: ChangeableSupplierStateHistoryProps) {
  const data: Array<SupplierStateHistoryRow> = useMemo(
    () =>
      versorgerstatusPeriods.map((period) => ({
        firstDate: period.firstDate,
        lastDate: period.lastDate,
        statusLabel: getSupplierStatusLabel(period.status)
      })),
    [versorgerstatusPeriods]
  );

  const columns: Array<MRT_ColumnDef<SupplierStateHistoryRow>> = [
    {
      header: "Von",
      accessorKey: "firstDate",
      accessorFn: (row) =>
        backendDateOrDateTimeToLuxonDateTimeOrNull(row.firstDate) ??
        "Unbekannt",
      Cell: ({ row }) => row.original.firstDate,
      size: 85
    },
    {
      header: "Bis",
      accessorKey: "lastDate",
      size: 85,
      Cell: ({ renderedCellValue }) => renderedCellValue ?? "Aktuell"
    },
    {
      header: "Versorgerstatus",
      accessorKey: "statusLabel"
    }
  ];

  const mrtTable = useMantineReactTable<SupplierStateHistoryRow>({
    ...getMinimalMRTOptions({
      initialState: { sorting: [{ id: "firstDate", desc: true }] },
      emptyRowsFallbackText: "Kein Versorgerstatus hinterlegt"
    }),
    columns,
    data
  });

  return <MantineReactTable table={mrtTable} />;
}

export { ChangeableSupplierStateHistory };
